import type { OrderItem, OrderShelfLocationMap } from '../../../types/apiTypes';

export interface ShelfLocationLookup {
  shelfIndex: string | null;
  type: string | null;
}

export const parseShelfIndex = (shelfIndex: string | undefined | null): [number, string, number] => {
  if (!shelfIndex || shelfIndex === 'N/A') {
    return [Infinity, 'Z', Infinity];
  }
  const [section, row, position] = shelfIndex.split('-');
  return [
    parseInt(section) || 0,
    row || 'Z',
    parseInt(position) || 0
  ];
};

export const compareShelfIndices = (a: string | undefined | null, b: string | undefined | null): number => {
  const [aSection, aRow, aPosition] = parseShelfIndex(a);
  const [bSection, bRow, bPosition] = parseShelfIndex(b);

  if (aSection !== bSection) return aSection - bSection;
  if (aRow !== bRow) return aRow.localeCompare(bRow);
  return aPosition - bPosition;
};

/** Parent rows that group component pick lines. */
export function getBundleParentIds(orderItems: OrderItem[]): Set<string> {
  const ids = orderItems
    .filter((item) => item.isComponent && item.parentId)
    .map((item) => item.parentId as string);
  return new Set(ids);
}

/** A parent row is either flagged or referenced as the parent of a component line. */
export function isBundleParentRow(item: OrderItem, parentIds: Set<string>): boolean {
  return item.isBundleParent === true || parentIds.has(item.id);
}

export function getShelfLookupSkus(orderItems: OrderItem[]): string[] {
  return [...new Set(
    orderItems
      .map((item) => item.sku?.toUpperCase())
      .filter(Boolean),
  )] as string[];
}

/** Shelf lookup has not written a resolved index yet (unset or ellipsis). */
export function isShelfLookupPending(shelfIndex: string | null | undefined): boolean {
  if (shelfIndex == null) return true;
  return shelfIndex.trim().toUpperCase() === '…';
}

/** Parent shelf is a real pick-bin or stock index, not N/A or a pending placeholder. */
export function hasLocatedParentShelf(shelfIndex: string | null | undefined): boolean {
  if (shelfIndex == null) return false;
  const normalized = shelfIndex.trim().toUpperCase();
  if (normalized === '' || normalized === 'N/A' || normalized === 'NA' || normalized === '…') {
    return false;
  }
  return true;
}

export async function loadOrderShelfLocationMap(
  skus: readonly string[],
  fetchMap: (skus: readonly string[]) => Promise<OrderShelfLocationMap>,
): Promise<{ readonly map: OrderShelfLocationMap; readonly loaded: boolean }> {
  if (skus.length === 0) {
    return { map: {}, loaded: true };
  }
  try {
    const map = await fetchMap(skus);
    return { map, loaded: true };
  } catch (error: unknown) {
    console.error('Error loading shelf locations for order items:', error);
    return { map: {}, loaded: false };
  }
}

export function applyShelfMapToOrderItems(
  items: readonly OrderItem[],
  shelfMap: OrderShelfLocationMap,
  loaded: boolean,
): OrderItem[] {
  if (!loaded) {
    return items.map((item) => ({ ...item }));
  }
  return items.map((item) => {
    const skuKey = item.sku?.toUpperCase();
    const loc = skuKey
      ? shelfMap[skuKey] ?? { shelfIndex: null, type: null }
      : { shelfIndex: null, type: null };
    return applyShelfLocationToOrderItem(item, loc);
  });
}

export function applyShelfLocationToOrderItem(
  item: OrderItem,
  loc: ShelfLocationLookup,
): OrderItem {
  const shelfIndex = loc.shelfIndex || 'N/A';
  const isStock = loc.type === 'stock';
  const shelfDisplay = loc.shelfIndex
    ? (isStock ? { stockLocation: loc.shelfIndex } : loc.shelfIndex)
    : null;
  return { ...item, shelfIndex, shelfDisplay };
}

function minShelfIndex(
  shelfIndices: Array<string | undefined | null>,
): string | undefined | null {
  if (shelfIndices.length === 0) return undefined;
  return shelfIndices.reduce((best, current) => {
    if (best == null) return current;
    if (current == null) return best;
    return compareShelfIndices(current, best) < 0 ? current : best;
  }, shelfIndices[0]);
}

export const getSortedItems = (orderItems: OrderItem[]): OrderItem[] => {
  const mainItems = orderItems.filter(item => !item.isComponent);
  const components = orderItems.filter(item => item.isComponent);
  const bundleParentIds = getBundleParentIds(orderItems);

  const sortedMainItems = [...mainItems].sort((a, b) => {
    const parentShelf = (item: OrderItem): string | undefined | null => {
      if (!bundleParentIds.has(item.id)) return item.shelfIndex;
      if (item.shelfIndex && item.shelfIndex !== 'N/A') return item.shelfIndex;
      return minShelfIndex(
        components.filter((c) => c.parentId === item.id).map((c) => c.shelfIndex),
      );
    };
    return compareShelfIndices(parentShelf(a), parentShelf(b));
  });

  const sortedItems = sortedMainItems.reduce((acc: OrderItem[], mainItem) => {
    acc.push(mainItem);
    const itemComponents = components
      .filter((comp) => comp.parentId === mainItem.id)
      .sort((a, b) => compareShelfIndices(a.shelfIndex, b.shelfIndex));
    acc.push(...itemComponents);
    return acc;
  }, []);

  const attachedComponentIds = new Set(
    sortedItems.filter((item) => item.isComponent).map((item) => item.id),
  );
  const orphanComponents = components
    .filter((comp) => !attachedComponentIds.has(comp.id))
    .sort((a, b) => compareShelfIndices(a.shelfIndex, b.shelfIndex));

  return [...sortedItems, ...orphanComponents];
};
